import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { Box, LinearProgress } from "@mui/material";
import { logger } from "@platform/browser-logger";
import { BTX_CURRENT_CARRIER_POLICY_DROPDOWN } from "@platform/feature-flags";
import { useAgGridState } from "@platform/hooks";
import {
  ColDef,
  ColGroupDef,
  ColumnMovedEvent,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetMainMenuItems,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridState,
  ICellRendererParams,
  MenuItemDef,
  SideBarDef,
} from "ag-grid-community";
import { AllEnterpriseModule } from "ag-grid-enterprise";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { gridLocaleText } from "@platform/design-system";
import {
  ComponentType,
  CSSProperties,
  PropsWithChildren,
  Ref,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { findNewPlansFromBpg } from "../../util/bpgMerge";
import { useUpdateCommission } from "../../api/commissionMutation";

import { AgeReductionScheduleEditor } from "./components/CellEditors/AgeReductionScheduleEditor";
import { BenefitDescriptionMultiOptionsEditor } from "./components/CellEditors/BenefitDescriptionMultiOptionsEditor";
import { ChildAgeBandsEditor } from "./components/CellEditors/ChildAgeBandsEditor";
import { EligibilityClassesEditor } from "./components/CellEditors/EligibilityClassesEditor";
import { GradedCommissionEditor } from "./components/CellEditors/GradedCommissionEditor";
import { HeapedCommissionEditor } from "./components/CellEditors/HeapedCommissionEditor";
import { PopoverListEditor } from "./components/CellEditors/PopoverListEditor";
import { PopoverSelectEditor } from "./components/CellEditors/PopoverSelectEditor";
import { PreExistingConditionMonthsEditor } from "./components/CellEditors/PreExistingConditionMonthsEditor";
import { ServiceWaitingPeriodsEditor } from "./components/CellEditors/ServiceWaitingPeriodsEditor";
import { AgGridProvider } from "./context/AgGridContext";
import { enforceBenefitsColumnFirst } from "./utilities/benefitsColumnInvariant";
import {
  benefitDefinitionsCache,
  buildEligibilityLookup,
  buildRowDataFromDependencies,
  loadBenefitDefinitionsAsync,
} from "./utilities/dataLoaders";
import { scaleColumnDefWidths } from "./utilities/scaleColumnDefWidths";
import { useValidateField } from "./validation/ValidationContext";
import { CurrentPolicyGroupHeader } from "./components/CurrentPolicyGroupHeader";
import "./plan-editor.css";
import {
  BenefitDescriptionMultiOptionsRenderer,
  EligibilityClassesRenderer,
  PopoverListRenderer,
  PopoverSelectRenderer,
  PreExistingConditionMonthsRenderer,
  ServiceWaitingPeriodsRenderer,
} from "./components/CellRenderers";
import { useFieldMetadata } from "./context/FieldMetadataContext";
import {
  buildColumns,
  type PlanDocumentMap,
} from "./utilities/buildPlanGroupColumns";
import { resolveConfigFlags } from "./utilities/resolveConfigFlags";
import {
  EditColumnStrategy,
  type ColumnBuildStrategy,
} from "./utilities/columnBuildStrategy";
import { createGridOptionsWithHandlers } from "./utilities/gridOptions";
import {
  seedManualWidths,
  useResizedColumnWidths,
} from "./utilities/useResizedColumnWidths";
import { useSyncedHorizontalWheel } from "./utilities/useSyncedHorizontalWheel";

import type { MpxAgGridStateProps } from "@platform/hooks";
import type { BenefitPlanGroup, RfpCoverage } from "../../api/types";
import type { BenefitDefinition } from "../../benefits/benefit.types";
import type { CommissionUpdateRequest } from "../../api/commissionMutation";
import type { PlanEditorConfiguration } from "./types/plan-editor.types";

const resolveGridStateId = (
  gridId: string | undefined,
  coverageType: string | undefined,
): string => gridId ?? `plan-editor-${coverageType ?? "default"}`;

const useGetRendererComponents = (
  CellRenderer?: ComponentType<PropsWithChildren<ICellRendererParams>>,
): AgGridReactProps["components"] =>
  useMemo(() => {
    if (!CellRenderer) {
      return {
        PopoverSelectRenderer,
        PopoverListRenderer,
        PreExistingConditionMonthsRenderer,
        EligibilityClassesRenderer,
        ServiceWaitingPeriodsRenderer,
        BenefitDescriptionMultiOptionsRenderer,
      };
    }

    return {
      PopoverSelectRenderer: wrapWithCellRenderer(
        CellRenderer,
        PopoverSelectRenderer,
      ),
      PopoverListRenderer: wrapWithCellRenderer(
        CellRenderer,
        PopoverListRenderer,
      ),
      PreExistingConditionMonthsRenderer: wrapWithCellRenderer(
        CellRenderer,
        PreExistingConditionMonthsRenderer,
      ),
      EligibilityClassesRenderer: wrapWithCellRenderer(
        CellRenderer,
        EligibilityClassesRenderer,
      ),
      ServiceWaitingPeriodsRenderer: wrapWithCellRenderer(
        CellRenderer,
        ServiceWaitingPeriodsRenderer,
      ),
      BenefitDescriptionMultiOptionsRenderer: wrapWithCellRenderer(
        CellRenderer,
        BenefitDescriptionMultiOptionsRenderer,
      ),
      [CellRenderer.displayName ?? CellRenderer.name]: CellRenderer,
    };
  }, [CellRenderer]);

export const wrapWithCellRenderer = <
  P extends PropsWithChildren<ICellRendererParams>,
>(
  CellRenderer: ComponentType<P>,
  WrappedRenderer: ComponentType<P>,
): ComponentType<P> => {
  const Wrapper = (params: P): React.JSX.Element => (
    <CellRenderer {...params}>
      <WrappedRenderer {...params} />
    </CellRenderer>
  );

  Wrapper.displayName = `CellRendererWrapper(${WrappedRenderer.displayName ?? WrappedRenderer.name})`;

  return Wrapper;
};

export interface PlanEditorProps {
  ref?: Ref<AgGridReact>;
  coverage?: RfpCoverage;
  /**
   * Callback to get the latest coverage from parent's ref (not the stale prop).
   *
   * CRITICAL: This prevents data loss during rapid edits. Without this, the second edit
   * would read from the stale coverage prop and overwrite the first edit. The parent
   * should maintain a ref to the latest coverage and return it via this callback.
   */
  getCurrentCoverage?: () => RfpCoverage | undefined;
  configuration?: PlanEditorConfiguration; // future of passing in other config
  /**
   * ppx-quote-history: stamps a root modifier the quote-history styling hangs off, so a
   * quote's state treatment cannot reach a surface that has not opted into it.
   *
   * It is a prop rather than a class the column builders emit, because the cell-level
   * classes are set from a plan group's own `isPendingRequote`/`isHistorical`, which are
   * resolved from proposal data with no knowledge of the flag. Scoping at the root gates
   * them all, and keeps the treatment off the Build surface, which renders this same grid
   * and has no notion of quote history.
   */
  quoteHistoryEnabled?: boolean;
  /**
   * MP configuration field keys the broker's configuration shows for this
   * coverage (CD-386). Opt-in — omitted, every row renders, which is what the
   * Build tab does today.
   */
  visibleMpConfigKeys?: ReadonlySet<string>;
  sx?: Record<string, unknown>; // Allow sx prop for Box styling customization
  onCoverageChange?: (updatedCoverage: RfpCoverage) => void; // Callback when coverage data changes
  onPlanReorder?: (planOrder: string[]) => void; // Callback when plan columns are reordered
  onPlanNicknameChange?: (planId: string, nickname: string) => void; // Callback when plan nickname is changed
  onGridReady?: (gridApi: GridApi) => void; // Callback when grid is ready, provides GridAPI for aligned grids
  /**
   * Reports the metrics aligned grids (RatesGrid/SummaryGrid) need to track this
   * grid exactly:
   * - `columnWidths`: rendered plan-column widths (colId → px), applied as
   *   explicit widths so aligned grids don't flex-size independently (which
   *   diverges when PlanEditor's flex viewport is narrowed by its scrollbar or
   *   sidebar).
   * - `rightGutter`: px occupied by PlanEditor's sidebar, which narrows its body
   *   and shortens its max horizontal scroll. Aligned grids reserve the same
   *   gutter so columns stay aligned when scrolled to the right edge.
   */
  onAlignmentMetricsChange?: (metrics: {
    columnWidths: Record<string, number>;
    rightGutter: number;
  }) => void;
  /** Refs to other AG Grid instances to align with (e.g., RatesGrid, SummaryGrid) */
  alignedGrids?: RefObject<AgGridReact>[];
  /**
   * Disable network sub-columns (in-network/out-of-network) for dental PPO.
   * Enable this when using alignedGrids with RatesGrid/SummaryGrid to ensure
   * column structures match for proper alignment.
   */
  disableNetworkColumns?: boolean;
  gridId?: string; // Optional unique ID for state persistence (defaults to coverage ID)
  enableStatePersistence?: boolean; // Whether to enable grid state persistence (default: true)
  statePersistenceStorageType?: "local" | "session"; // Storage type for state persistence (default: "session")
  benefitPlanGroups?: BenefitPlanGroup[]; // Optional benefit plan groups from API
  /** Optional map of plan UUID → source document filename for document-level column grouping. */
  planDocumentMap?: PlanDocumentMap;
  /**
   * Explicit seam for the BTX-only Plan Level dropdown (PlanLevelHeader):
   * BTX sets this true. Must never be inferred from planDocumentMap — MPX's
   * carrier-grouped grid also supplies a planDocumentMap, but for its own
   * unrelated "Source Document" header labels, not this dropdown.
   */
  enablePlanLevelDropdown?: boolean;
  // Generic wrapper for entire cell content, e.g. for adding SBS corners or AI status overlays
  CellRenderer?: ComponentType<PropsWithChildren<ICellRendererParams>>;
  /** Optional row-data transformer (e.g., for mocked/test decorations). */
  transformRowData?: (rowData: unknown[]) => unknown[];
  /** Optional context menu builder. When provided, replaces the default (empty) menu. */
  getContextMenuItems?: (
    params: GetContextMenuItemsParams,
  ) => (DefaultMenuItem | MenuItemDef)[];
  /**
   * Callback when carrier column groups are reordered via drag (for carrier
   * sequence). `movedCarrierId` is the specific carrier the user dragged, or
   * null if it couldn't be determined — the resulting `carrierIds` order
   * alone can't always say which carrier moved (e.g. an adjacent two-carrier
   * swap reads the same either way).
   */
  onCarrierReorder?: (
    carrierIds: string[],
    movedCarrierId: string | null,
  ) => void;
  /**
   * Called once a user drag of a column or column group has finished, before
   * the plan and carrier order are read back from the grid. A surface uses it
   * to adjust the dropped columns, e.g. to keep a carrier's versions together.
   */
  onUserColumnMoved?: (event: ColumnMovedEvent) => void;
  getSidebar?: (rfp: RfpCoverage | undefined) => SideBarDef | false;
  getMainMenuItems?: GetMainMenuItems;
  getPlanLevelMenuItem?: GetMainMenuItems;
  /** When true, shows a loading overlay (e.g. while a plan mutation is in flight). */
  isMutating?: boolean;
  /** Optional MPX-persisted AG Grid state (initialState, onStateUpdated, key). */
  gridStateProps?: MpxAgGridStateProps;
  /** Scales plan-column widths (1 = default). Drives the narrow/medium/wide preset. */
  columnWidthScale?: number;
  /**
   * Bump on every width-preset click to reset all columns to their def sizing,
   * discarding manual drags — including a click on the already-active preset,
   * which changes no other prop.
   */
  columnSizingResetToken?: number;
  /**
   * Column-build strategy. Defaults to the edit-mode strategy; a consumer (e.g.
   * the Quotes SBS grid) injects an alternative to change leaf-cell behavior
   * without core knowing the feature.
   */
  columnStrategy?: ColumnBuildStrategy;
  /**
   * Column-definition builder. Defaults to the edit-mode column layout; a
   * consumer injects an alternative (e.g. the Quotes tab's carrier-grouped
   * columns) using the grid context core provides.
   */
  buildColumnDefs?: (ctx: BuildColumnDefsContext) => (ColDef | ColGroupDef)[];
}

/** Grid context core supplies to an injected {@link PlanEditorProps.buildColumnDefs}. */
export interface BuildColumnDefsContext {
  coverage: RfpCoverage;
  benefitPlanGroups?: BenefitPlanGroup[];
  disableNetworkColumns: boolean;
  columnStrategy: ColumnBuildStrategy;
  planDocumentMap?: PlanDocumentMap;
}

const planEditorClassName = (
  coverage: RfpCoverage | undefined,
  quoteHistoryEnabled: boolean | undefined,
  stickyHeader: boolean,
): string =>
  [
    "plan-editor",
    coverage?.type === "vision" && "plan-editor--vision",
    quoteHistoryEnabled && "plan-editor--quote-history",
    stickyHeader && "plan-editor--sticky-header",
  ]
    .filter(Boolean)
    .join(" ");

export const PlanEditor = ({
  ref,
  coverage,
  getCurrentCoverage,
  configuration,
  quoteHistoryEnabled,
  visibleMpConfigKeys,
  sx,
  onCoverageChange,
  onPlanReorder,
  onGridReady: onGridReadyCallback,
  onAlignmentMetricsChange,
  alignedGrids = [],
  disableNetworkColumns = false,
  gridId,
  enableStatePersistence = true,
  statePersistenceStorageType = "session",
  benefitPlanGroups,
  planDocumentMap,
  enablePlanLevelDropdown = false,
  CellRenderer,
  transformRowData,
  getContextMenuItems,
  onCarrierReorder,
  onUserColumnMoved,
  getSidebar,
  getMainMenuItems,
  getPlanLevelMenuItem,
  isMutating = false,
  gridStateProps,
  columnWidthScale,
  columnSizingResetToken,
  columnStrategy: injectedColumnStrategy,
  buildColumnDefs,
}: PlanEditorProps): React.JSX.Element => {
  const { useAutoHeight, stickyHeader, stickyHeaderTop } =
    resolveConfigFlags(configuration);

  // When the Current Carrier - Policy feature is on, the per-plan "Current
  // Carrier" row is removed and the policy is selected at the group level
  // (ADR 10.22).
  const currentCarrierPolicyDropdownEnabled = useFeatureIsOn(
    BTX_CURRENT_CARRIER_POLICY_DROPDOWN,
  );

  const columnStrategy = useMemo(
    () => injectedColumnStrategy ?? new EditColumnStrategy(CellRenderer),
    [injectedColumnStrategy, CellRenderer],
  );

  const gridRef = useRef<AgGridReact>(null);
  // Wraps the AG Grid so its sidebar can be measured for aligned-grid gutter sync.
  const containerRef = useRef<HTMLDivElement>(null);
  useSyncedHorizontalWheel(containerRef);

  // Expose the grid ref to parent via forwardRef
  useImperativeHandle(ref, () => gridRef.current as AgGridReact);

  // Capture initialState at mount time. Using a ref rather than including
  // gridStateProps?.initialState in onGridReady's deps prevents the callback
  // from recreating (and gridOptions from recomputing) every time PUT 200
  // updates the React Query cache mid-session.
  const initialStateRef = useRef(gridStateProps?.initialState);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const isGridReady = gridApi !== null;
  const [benefitDefinitions, setBenefitDefinitions] = useState<
    Record<string, BenefitDefinition>
  >({});
  // Source of truth for row data
  const [rowData, setRowData] = useState<unknown[]>([]);
  // Track last loaded coverage type to prevent unnecessary reloads
  const lastCoverageTypeRef = useRef<string | null>(null);
  // Track if we're updating from internal cell edit to prevent rowData rebuild
  const isInternalUpdateRef = useRef(false);
  // Track if data is being processed for loading indicator
  const [isProcessingGridData, setIsProcessingGridData] = useState(false);

  // Get validation function from context. ValidationProvider rebuilds it every
  // render, so gridOptions reads it through a ref: a new gridOptions makes AG
  // Grid re-apply defaultColDef to every column.
  const { validateField } = useValidateField();
  const validateFieldRef = useRef(validateField);
  validateFieldRef.current = validateField;
  const stableValidateField = useCallback<typeof validateField>(
    (...args) => validateFieldRef.current(...args),
    [],
  );

  // Get field metadata for tracking manual edits
  const { markManuallyEdited, handleAiFieldEdited } = useFieldMetadata();

  // Commission mutation hook
  const updateCommission = useUpdateCommission();

  // When gridStateProps is provided, the caller owns persistence via MPX
  // (server-side). Disable local browser-storage persistence to avoid two
  // systems writing on every column event and racing each other.
  const useLocalPersistence = enableStatePersistence && !gridStateProps;
  const gridStateId = resolveGridStateId(gridId, coverage?.type);
  const onStateRestored = useCallback(() => {
    const { api } = gridRef.current ?? {};
    enforceBenefitsColumnFirst(api);
    if (api) seedManualWidths(api);
    logger.info(`[PlanEditor] Grid state restored for ${gridStateId}`);
  }, [gridStateId]);
  const onStateSaved = useCallback(() => {
    logger.info(`[PlanEditor] Grid state saved for ${gridStateId}`);
  }, [gridStateId]);
  const gridStatePersistence = useAgGridState({
    gridId: gridStateId,
    storageType: statePersistenceStorageType,
    persistColumnState: useLocalPersistence,
    persistFilterState: useLocalPersistence,
    persistSortState: useLocalPersistence,
    persistColumnGroupState: useLocalPersistence,
    debounceMs: 500,
    onStateRestored,
    onStateSaved,
  });
  // Keep a ref so onGridReady can call gridStatePersistence.onGridReady without
  // including the whole object in its dep array. Without this, the inline
  // onStateRestored/onStateSaved callbacks above cause gridStatePersistence to
  // be a new object every render, which recreates onGridReady, which recreates
  // gridOptions, which causes api.updateGridOptions to re-apply defaultColDef
  // (including flex: 1) and snap columns back after every PUT 200 response.
  const gridStatePersistenceRef = useRef(gridStatePersistence);
  gridStatePersistenceRef.current = gridStatePersistence;

  // Clear persisted grid state when column structure changes (plan add/delete/duplicate)
  // Prevents stale column widths/order from being applied to a different column structure
  const { clearState } = gridStatePersistence;
  const prevBpgCountRef = useRef(benefitPlanGroups?.length);
  useEffect(() => {
    const prevCount = prevBpgCountRef.current;
    const currentCount = benefitPlanGroups?.length;
    // Only clear when both counts are defined numbers (skip undefined during loading)
    if (
      typeof prevCount === "number" &&
      typeof currentCount === "number" &&
      prevCount !== currentCount
    ) {
      clearState();
    }
    prevBpgCountRef.current = currentCount;
  }, [benefitPlanGroups?.length, clearState]);

  const resolvedGetMainMenuItems = useCallback<GetMainMenuItems>(
    (params) =>
      getMainMenuItems
        ? getMainMenuItems(params)
        : (params.defaultItems as (DefaultMenuItem | MenuItemDef)[]),
    [getMainMenuItems],
  );

  const { mutate: mutateCommission } = updateCommission;
  const onCommissionChange = useCallback(
    (request: CommissionUpdateRequest) => {
      mutateCommission(request);
    },
    [mutateCommission],
  );

  //  Load benefit definitions and manage data flow
  useEffect(() => {
    // Wait for GridApi to be initialized before doing anything else
    if (!coverage?.type) {
      return;
    }

    const loadKey = coverage.type;
    if (lastCoverageTypeRef.current === loadKey) {
      return;
    }

    setIsProcessingGridData(true);
    // Check cache before setting loading state to avoid unnecessary UI updates
    if (benefitDefinitionsCache.has(loadKey)) {
      const cachedDefs = benefitDefinitionsCache.get(loadKey);
      setBenefitDefinitions(cachedDefs || {});
      lastCoverageTypeRef.current = loadKey;
      return;
    }

    const loadBenefitDefinitions = async (): Promise<void> => {
      try {
        const benDefs = await loadBenefitDefinitionsAsync(coverage.type);
        setBenefitDefinitions(benDefs || {});

        // Track that we've loaded this coverage type
        lastCoverageTypeRef.current = loadKey;
      } catch (error) {
        logger.error("Error loading benefit definitions:", error as Error);
        // If benefit definition loading fails, no grid data can be built
        setIsProcessingGridData(false);
      }
    };

    loadBenefitDefinitions();
  }, [coverage?.type]);

  // Merge data from the benefit-plan-groups API into the RFP coverage.
  //
  // The BPG response carries two pieces of data the RFP endpoint may lack:
  //  1. coverage.benefitClasses – can be newer than the RFP copy when a
  //     benefit class was just created.
  //  2. plan-level eligibilityClasses – the BPG endpoint returns the
  //     assigned eligibility classes per plan, while the RFP endpoint
  //     may not yet include them.
  //
  // We merge both so the grid always renders the latest data.
  const coverageWithBpgData = useMemo(() => {
    if (!coverage) return coverage;

    let changed = false;

    // --- 1. Merge coverage-level benefitClasses ---
    // BPG data is the authoritative source for benefit classes because it
    // reflects the latest creates, renames, and deletes from Class Manager.
    const bpgClasses = benefitPlanGroups?.[0]?.coverage?.benefitClasses ?? [];

    let mergedBenefitClasses = coverage.benefitClasses ?? [];
    if (bpgClasses.length > 0) {
      const existingClasses = coverage.benefitClasses ?? [];
      const classesMatch =
        bpgClasses.length === existingClasses.length &&
        bpgClasses.every(
          (c, i) =>
            c.id === existingClasses[i].id &&
            c.name === existingClasses[i].name,
        );
      if (!classesMatch) {
        mergedBenefitClasses = bpgClasses;
        changed = true;
      }
    }

    // --- 2. Merge plan-level eligibilityClasses from BPG plans ---
    const bpgEligByPlanId = buildEligibilityLookup(benefitPlanGroups);

    let mergedBenefitPlans = coverage.benefitPlans;
    if (bpgEligByPlanId.size > 0 && coverage.benefitPlans) {
      mergedBenefitPlans = coverage.benefitPlans.map((plan) => {
        if (!plan.id) return plan;
        const bpgElig = bpgEligByPlanId.get(plan.id);
        if (!bpgElig) return plan;

        const currentElig = plan.eligibilityClasses ?? [];
        if (currentElig.length > 0) return plan; // already populated

        changed = true;
        return { ...plan, eligibilityClasses: bpgElig };
      });
    }

    // --- 3. Merge new benefit plans from BPG that aren't in coverage ---
    // When a plan is created/duplicated, the BPG query resolves before the
    // RFP query. This ensures new plans are present in coverage so that
    // onCellValueChanged can find and update them.
    const existingPlanIds = new Set(
      mergedBenefitPlans?.map((p) => p.id).filter((id): id is string => !!id),
    );
    const newPlans = findNewPlansFromBpg(benefitPlanGroups, existingPlanIds);
    if (newPlans.length > 0) {
      changed = true;
      mergedBenefitPlans = [...(mergedBenefitPlans || []), ...newPlans];
    }

    if (!changed) return coverage;

    return {
      ...coverage,
      benefitClasses: mergedBenefitClasses,
      benefitPlans: mergedBenefitPlans,
    };
  }, [coverage, benefitPlanGroups]);

  // Step 3: Build column definitions (depends on coverage and GridApi being ready)
  const columnDefs = useMemo((): (ColDef | ColGroupDef)[] => {
    if (!isGridReady || !coverageWithBpgData) {
      return [];
    }

    // A consumer (e.g. the Quotes carrier-grouped grid) may supply its own
    // column builder; otherwise use the default edit-mode columns.
    if (buildColumnDefs) {
      return buildColumnDefs({
        coverage: coverageWithBpgData,
        benefitPlanGroups,
        disableNetworkColumns,
        columnStrategy,
        planDocumentMap,
      });
    }

    return buildColumns(
      benefitPlanGroups,
      coverageWithBpgData,
      disableNetworkColumns,
      columnStrategy,
      planDocumentMap,
      getPlanLevelMenuItem,
      currentCarrierPolicyDropdownEnabled,
      enablePlanLevelDropdown,
    );
  }, [
    isGridReady,
    coverageWithBpgData,
    benefitPlanGroups,
    disableNetworkColumns,
    columnStrategy,
    planDocumentMap,
    enablePlanLevelDropdown,
    buildColumnDefs,
    getPlanLevelMenuItem,
    currentCarrierPolicyDropdownEnabled,
  ]);

  // Apply the narrow/medium/wide width preset by scaling plan-column widths.
  const scaledColumnDefs = useMemo(
    () => scaleColumnDefWidths(columnDefs, columnWidthScale ?? 1),
    [columnDefs, columnWidthScale],
  );

  const captureResizedWidths = useResizedColumnWidths(
    gridApi,
    scaledColumnDefs,
    columnWidthScale ?? 1,
    columnSizingResetToken,
  );

  // After column structure changes (add/delete/duplicate), force AG Grid to
  // re-render all rows so that colSpan functions re-evaluate against the new
  // column layout. This is lightweight compared to a full column rebuild.
  const prevColCountRef = useRef(columnDefs.length);
  useEffect(() => {
    if (!gridApi || columnDefs.length === 0) return;
    enforceBenefitsColumnFirst(gridApi);
    if (
      prevColCountRef.current !== columnDefs.length &&
      prevColCountRef.current > 0
    ) {
      gridApi.redrawRows();
    }
    prevColCountRef.current = columnDefs.length;
  }, [gridApi, columnDefs.length]);

  // Build row data and manage loading state
  const prevRowDataColumnCountRef = useRef(columnDefs.length);
  useEffect(() => {
    const structureChanged =
      prevRowDataColumnCountRef.current !== columnDefs.length;
    prevRowDataColumnCountRef.current = columnDefs.length;

    // Skip rebuilding rowData for an internal cell-edit echo, but never when
    // the column structure just changed (plan added/duplicated/deleted) —
    // that always needs a full rebuild.
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      if (!structureChanged) {
        return;
      }
    }

    // Early exit if dependencies aren't ready to avoid unnecessary processing
    if (
      !coverageWithBpgData ||
      !benefitDefinitions ||
      Object.keys(benefitDefinitions).length === 0 ||
      columnDefs.length === 0
    ) {
      setRowData([]);
      return;
    }

    // Build row data
    const newRowData = buildRowDataFromDependencies(
      coverageWithBpgData,
      benefitDefinitions,
      columnDefs,
      currentCarrierPolicyDropdownEnabled,
      visibleMpConfigKeys,
    );

    let finalRowData = newRowData;
    if (transformRowData) {
      try {
        finalRowData = transformRowData(newRowData);
      } catch (error) {
        logger.error(
          "[PlanEditor] Error transforming row data:",
          error as Error,
        );
      }
    }

    setRowData(finalRowData);
    setIsProcessingGridData(false);

    // AG Grid's immutable rowData diff can skip repainting a cell when the
    // popup editor's in-place `node.setDataValue` already mutated the matched
    // row before this rebuild: the new rowData then compares equal to the
    // already-mutated node, so the cell never repaints and the edit only
    // surfaces on the next server refetch. Push the same rows through a
    // transaction to force the changed cells to refresh now. Restricted to rows
    // AG Grid already holds so structural changes stay with the rowData prop.
    if (gridApi) {
      const updates = finalRowData.filter(
        (row) =>
          gridApi.getRowNode((row as { benefitKey: string }).benefitKey) !=
          null,
      );
      if (updates.length > 0) gridApi.applyTransaction({ update: updates });
    }
  }, [
    isGridReady,
    gridApi,
    coverageWithBpgData,
    benefitDefinitions,
    columnDefs,
    transformRowData,
    currentCarrierPolicyDropdownEnabled,
    visibleMpConfigKeys,
  ]);

  // Event handlers (defined before gridOptions)
  const emitAlignmentMetrics = useCallback(
    (api: GridApi) => {
      if (!onAlignmentMetricsChange) return;
      const columnWidths: Record<string, number> = {};
      for (const column of api.getAllDisplayedColumns()) {
        columnWidths[column.getColId()] = column.getActualWidth();
      }
      const sideBarEl =
        containerRef.current?.querySelector<HTMLElement>(".ag-side-bar");
      const rightGutter = sideBarEl
        ? sideBarEl.getBoundingClientRect().width
        : 0;
      onAlignmentMetricsChange({ columnWidths, rightGutter });
    },
    [onAlignmentMetricsChange],
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      setGridApi(params.api);

      // Initialize local state persistence only when MPX gridStateProps is absent
      if (useLocalPersistence) {
        gridStatePersistenceRef.current.onGridReady(params);
      }

      // Apply MPX-persisted state imperatively rather than via the initialState
      // prop. AG Grid re-applies initialState on every prop reference change
      // (it is not excluded from prop-change detection), so a server cache
      // update after PUT 200 would snap column widths back. Calling applyState
      // once here is a one-shot operation that AG Grid never repeats.
      if (initialStateRef.current) {
        params.api.setState(initialStateRef.current as GridState);
        seedManualWidths(params.api);
      }

      // Call parent's onGridReady callback if provided
      onGridReadyCallback?.(params.api);
    },
    [useLocalPersistence, onGridReadyCallback],
  );

  const resolvedSideBar = useMemo(
    () => (getSidebar ? getSidebar(coverageWithBpgData) : false),
    [getSidebar, coverageWithBpgData],
  );

  // Memoized grid options with event handlers
  const gridOptions = useMemo((): GridOptions => {
    const baseOptions = createGridOptionsWithHandlers(
      onGridReady,
      resolvedGetMainMenuItems,
      onCoverageChange,
      onPlanReorder,
      coverageWithBpgData || undefined,
      getCurrentCoverage,
      useLocalPersistence ? gridStatePersistence.saveState : undefined,
      isInternalUpdateRef,
      stableValidateField,
      onCommissionChange,
      resolvedSideBar,
      onCarrierReorder,
      columnStrategy.getDefaultColDefOverrides(),
      markManuallyEdited,
      handleAiFieldEdited,
      onUserColumnMoved,
    );

    // Re-emit rendered column widths whenever they can change, so aligned grids
    // stay in sync. Flex sizing recomputes on viewport/scrollbar/sidebar changes
    // (onGridSizeChanged), on column set changes (onDisplayedColumnsChanged),
    // after the first render (onFirstDataRendered), and on manual resize
    // (onColumnResized). Each wrapper preserves the base handler.
    return {
      ...baseOptions,
      onFirstDataRendered: (event) => {
        baseOptions.onFirstDataRendered?.(event);
        emitAlignmentMetrics(event.api);
      },
      onGridSizeChanged: (event) => {
        baseOptions.onGridSizeChanged?.(event);
        emitAlignmentMetrics(event.api);
      },
      onDisplayedColumnsChanged: (event) => {
        baseOptions.onDisplayedColumnsChanged?.(event);
        emitAlignmentMetrics(event.api);
      },
      onColumnResized: (event) => {
        baseOptions.onColumnResized?.(event);
        captureResizedWidths(event);
        // AG Grid fires this continuously while dragging a resize handle; only
        // recompute widths for all displayed columns once the drag finishes.
        if (event.finished) {
          emitAlignmentMetrics(event.api);
        }
      },
      onToolPanelVisibleChanged: (event) => {
        baseOptions.onToolPanelVisibleChanged?.(event);
        emitAlignmentMetrics(event.api);
      },
    };
  }, [
    onGridReady,
    resolvedGetMainMenuItems,
    onCoverageChange,
    onPlanReorder,
    coverageWithBpgData,
    getCurrentCoverage,
    useLocalPersistence,
    gridStatePersistence.saveState,
    stableValidateField,
    onCommissionChange,
    resolvedSideBar,
    onCarrierReorder,
    onUserColumnMoved,
    columnStrategy,
    markManuallyEdited,
    handleAiFieldEdited,
    emitAlignmentMetrics,
    captureResizedWidths,
  ]);

  const rendererComponents = useGetRendererComponents(CellRenderer);

  // generic fix for allowing control over styles of box
  const mergedSx = {
    display: "flex",
    flexDirection: "column",
    height: useAutoHeight ? "auto" : "calc(100vh - 400px)",
    ...sx, // User-provided styles override defaults
  };

  return (
    <Box
      ref={containerRef}
      className={planEditorClassName(
        coverage,
        quoteHistoryEnabled,
        stickyHeader,
      )}
      data-grid-id="plan-editor"
      style={
        stickyHeader
          ? ({
              "--plan-editor-sticky-header-top": stickyHeaderTop,
            } as CSSProperties)
          : undefined
      }
      sx={{
        ...mergedSx,
        position: "relative",
        "& .ag-root-wrapper": {
          borderRadius: 0, // Remove border radius from AG Grid wrapper
        },
      }}
    >
      {isMutating && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }}
        />
      )}
      <AgGridProvider api={gridApi}>
        <AgGridReact
          key={gridStateProps?.key}
          ref={gridRef}
          modules={[AllEnterpriseModule]}
          domLayout={useAutoHeight ? "autoHeight" : "normal"}
          columnDefs={scaledColumnDefs}
          rowData={rowData}
          gridOptions={gridOptions}
          localeText={gridLocaleText}
          getMainMenuItems={resolvedGetMainMenuItems}
          getContextMenuItems={getContextMenuItems}
          alignedGrids={alignedGrids}
          loading={isProcessingGridData}
          onStateUpdated={gridStateProps?.onStateUpdated}
          components={{
            PopoverListEditor,
            AgeReductionScheduleEditor,
            ChildAgeBandsEditor,
            PopoverSelectEditor,
            PreExistingConditionMonthsEditor,
            BenefitDescriptionMultiOptionsEditor,
            EligibilityClassesEditor,
            HeapedCommissionEditor,
            GradedCommissionEditor,
            ServiceWaitingPeriodsEditor,
            CurrentPolicyGroupHeader,
            ...rendererComponents,
          }}
        />
      </AgGridProvider>
    </Box>
  );
};

// Set display name for better debugging
PlanEditor.displayName = "PlanEditor";
export default PlanEditor;
